import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import InputField from '../../../common_lib_front/components/inputField/inputField';
import PassInfo from '../../../common_lib_front/types/passInfo';
import { newVehicleInfoConfig, VehicleInfoConfig } from '../../../common_lib_front/types/vehicleInfo';
import style from './getPasses.module.css';
import { VehiclePass } from './types';
import { PassOptionErrorsType } from './validateVehicleData';

interface VehiclePassFormProps {
  vehiclePass: PassInfo;
  // vehiclePass: {
  //   passId: string;
  //   status: string;
  //   paid: string;
  //   passInfoId: string;
  // } & {
  //   vehicle?: VehiclePass;
  //   passInfo?: { priceInfo?: { chargeForFullCapacity: boolean } };
  // };
  passInfoId: string; // Add passInfoId as a prop to fetch vehicle data
  passIndex: number;
  handleChange: (passIndex: number, field: keyof VehiclePass, value: string) => void;
  handleCheckboxChange: (passIndex: number) => void;
  smsAlert: boolean;
  promotionalAlert: boolean;
  duration: number | undefined;
  initialStartDate: string;
  initialEndDate: string;
  maxEndDate: string;
  errors?: PassOptionErrorsType;
  setSmsAlert: (value: boolean) => void;
  setPromotionalAlert: (value: boolean) => void;
  disabled?: boolean;
}
const GET_VEHICLE_INFO_CONFIG = gql`
  query GetVehicleInfoConfig($passInfoId: String!) {
    getVehicleInfoConfig(passInfoId: $passInfoId) {
      success
      error
      data {
        passInfoId
        make
        vehicleModel
        type
        color
        licensePlate
        fleetNumber
        licensePlateState
        primaryDriverName
        licensePrimaryDriver
        completeLater
      }
    }
  }
`;

const errorMessages = {
  type: 'Vehicle Type is required.',
  make: 'Vehicle Make is required.',
  color: 'Vehicle Color is required.',
  licensePlate: 'License Plate is required.',
  vehicleModel: 'Vehicle Model is required.',
  fleetNumber: 'Fleet Number is required.',
  licensePlateState: 'License Plate State is required.',
  primaryDriverName: 'Primary Driver Name is required.',
  licensePrimaryDriver: 'Primary Driver License is required.',
};

const EditVehiclePassForm: React.FC<VehiclePassFormProps> = ({
  vehiclePass,
  passInfoId,
  passIndex,
  initialStartDate,
  initialEndDate,
  duration,
  errors,
  handleChange,
  handleCheckboxChange,
  disabled,
}) => {
  console.log('editVehiclePassForm passInfo:', passInfoId);
  const { data: vehicleInfoConfigData, loading, error } = useQuery(GET_VEHICLE_INFO_CONFIG, {
    variables: { passInfoId },
    fetchPolicy: 'network-only',
  });
  const [config, setConfig] = useState<VehicleInfoConfig>(newVehicleInfoConfig({}));
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [maxEndDate, setMaxEndDate] = useState(initialEndDate);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    handleChange(passIndex, 'startDate', newStartDate);

    let start;
    if (newStartDate) {
      start = new Date(newStartDate);
    } else {
      start = new Date();
    }
    const calculatedMaxEndDate = new Date(start);
    calculatedMaxEndDate.setDate(start.getDate() + (duration || 0));

    const formattedMaxEndDate = calculatedMaxEndDate.toISOString().split('T')[0];
    if (formattedMaxEndDate <= initialEndDate) {
      setMaxEndDate(formattedMaxEndDate);
      setEndDate(formattedMaxEndDate);
      handleChange(passIndex, 'endDate', formattedMaxEndDate);
    }
    // else {
    //   setMaxEndDate(initialEndDate);
    //   setEndDate(initialEndDate);
    //   handleChange(index, 'endDate', initialEndDate);
    // }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedEndDate = new Date(e.target.value);
    const start = startDate ? new Date(startDate) : new Date();
    const maxAllowedEndDate = new Date(start);
    maxAllowedEndDate.setDate(start.getDate() + (duration || 0));

    const maxAllowedFormattedDate = maxAllowedEndDate.toISOString().split('T')[0];

    // Ensure the end date is within the allowed range
    if (selectedEndDate <= maxAllowedEndDate && e.target.value <= initialEndDate) {
      setEndDate(e.target.value);
      handleChange(passIndex, 'endDate', e.target.value);
    } else {
      setEndDate(maxAllowedFormattedDate);
      handleChange(passIndex, 'endDate', maxAllowedFormattedDate);
    }
  };

  // Update maxEndDate whenever startDate or duration changes
  useEffect(() => {
    let newMaxEndDate;
    if (startDate) {
      newMaxEndDate = new Date(startDate);
    } else {
      newMaxEndDate = new Date();
    }
    newMaxEndDate.setDate(newMaxEndDate.getDate() + (duration || 0));

    const formattedMaxEndDate = newMaxEndDate.toISOString().split('T')[0];
    if (formattedMaxEndDate <= initialEndDate) {
      setMaxEndDate(formattedMaxEndDate);
    } else {
      setMaxEndDate(initialEndDate);
    }

    // Adjust endDate if it's beyond the allowed range
    if (new Date(endDate) > newMaxEndDate) {
      setEndDate(formattedMaxEndDate);
      handleChange(passIndex, 'endDate', formattedMaxEndDate);
    } else if (new Date(endDate) < new Date(initialStartDate)) {
      setEndDate(initialStartDate);
      handleChange(passIndex, 'endDate', initialStartDate);
    }
  }, [startDate, duration, initialEndDate, initialStartDate, handleChange, passIndex]);

  // Ensure startDate and endDate are set initially
  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    if (initialStartDate) {
      handleChange(passIndex, 'startDate', initialStartDate);
    } else {
      handleChange(passIndex, 'startDate', new Date().toISOString().split('T')[0]);
    }

    handleChange(passIndex, 'endDate', initialEndDate);
  }, [initialStartDate, initialEndDate]);

  useEffect(() => {
    if (vehicleInfoConfigData?.getVehicleInfoConfig?.success) {
      const vehicleData = vehicleInfoConfigData.getVehicleInfoConfig.data;
      setConfig(newVehicleInfoConfig(vehicleData));
    }
  }, [vehicleInfoConfigData]);

  if (loading) return <p>Loading vehicle information...</p>;
  if (error) return <p>Error loading vehicle information: {error.message}</p>;
  console.log('vehiclePass=====>', passInfoId, vehiclePass);

  const isRequired = (field: string) => config[field] === 'required';
  return (
    <div key={passInfoId} className={`${style.mainInnerBox}`}>
      <div className={style.passOptions}>
        <div className={style.vehiclePassForm}>
          <div className={style.vehicleCheckboxContainer}>
            <h4 className={style.vehicleInfoTitle}>Vehicle Details</h4>
          </div>
          <div className={style.vehicleBox}>
            {isRequired('type') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>Vehicle type</label>
                <select
                  required
                  className={style.vehicleOptionsInput}
                  value={vehiclePass.vehicle?.type}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e => handleChange(passIndex, 'type', e.target.value)}
                >
                  <option value="">Choose a vehicle type</option>
                  <option value="Coupe (2 Door Car)">Coupe (2 Door Car)</option>
                  <option value="Sedan (4 Door Car)">Sedan (4 Door Car)</option>
                  <option value="Sports Car">Sports Car</option>
                  <option value="Wagon/Station Wagon">Wagon/Station Wagon</option>
                  <option value="Sports Utility Vehicle (SUV)">
                    Sports Utility Vehicle (SUV)
                  </option>
                  <option value="Minivan">Minivan</option>
                  <option value="High Capacity Passenger Van">
                    High Capacity Passenger Van
                  </option>
                  <option value="Pickup Truck">Pickup Truck</option>
                  <option value="Bus">Bus</option>
                  <option value="Utility Truck">Utility Truck</option>
                  <option value="Other Comercial Vehicle (Box Truck/Semi)">
                    Other Comercial Vehicle (Box Truck/Semi)
                  </option>
                  <option value="Trailer">Trailer</option>
                  <option value="LSV">LSV</option>
                </select>
                {errors?.[passInfoId]?.[passIndex]?.includes('type') ? (
                  <span style={{ color: 'red' }}>{errorMessages.type}</span>
                ) : null}
              </div>
            )}
            {isRequired('make') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>Vehicle make</label>
                <input
                  required
                  className={style.vehicleOptionsInput}
                  type="text"
                  value={vehiclePass.vehicle?.make}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e => handleChange(passIndex, 'make', e.target.value)}
                  placeholder="Enter vehicle make"
                />
                {errors?.[passInfoId]?.[passIndex]?.includes('make') ? (
                  <span style={{ color: 'red' }}>{errorMessages.make}</span>
                ) : null}
              </div>
            )}
          </div>

          <div className={style.vehicleBox}>
            {isRequired('color') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>Vehicle color</label>
                <input
                  required
                  className={style.vehicleOptionsInput}
                  type="text"
                  value={vehiclePass.vehicle?.color}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e => handleChange(passIndex, 'color', e.target.value)}
                  placeholder="Enter vehicle color"
                />
                {errors?.[passInfoId]?.[passIndex]?.includes('color') ? (
                  <span style={{ color: 'red' }}>{errorMessages.color}</span>
                ) : null}
              </div>
            )}
            {isRequired('licensePlate') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>License plate</label>
                <input
                  required
                  className={style.vehicleOptionsInput}
                  type="text"
                  value={vehiclePass.vehicle?.licensePlate}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e => handleChange(passIndex, 'licensePlate', e.target.value)}
                  placeholder="Enter your license plate number"
                />
                {errors?.[passInfoId]?.[passIndex]?.includes('licensePlate') &&
                !vehiclePass.vehicle?.licensePlate ? (
                  <span style={{ color: 'red' }}>{errorMessages.licensePlate}</span>
                ) : null}
              </div>
            )}
          </div>

          <div className={style.vehicleBox}>
            {isRequired('vehicleModel') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>Vehicle Model</label>
                <input
                  required
                  className={style.vehicleOptionsInput}
                  type="text"
                  value={vehiclePass.vehicle?.vehicleModel}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e => handleChange(passIndex, 'vehicleModel', e.target.value)}
                  placeholder="Enter your vehicle model"
                />
                {errors?.[passInfoId]?.[passIndex]?.includes('vehicleModel') &&
                !vehiclePass.vehicle?.vehicleModel ? (
                  <span style={{ color: 'red' }}>{errorMessages.vehicleModel}</span>
                ) : null}
              </div>
            )}
            {isRequired('fleetNumber') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>Fleet Number</label>
                <input
                  required
                  className={style.vehicleOptionsInput}
                  type="text"
                  value={vehiclePass.vehicle?.fleetNumber}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e => handleChange(passIndex, 'fleetNumber', e.target.value)}
                  placeholder="Enter fleet number"
                />
                {errors?.[passInfoId]?.[passIndex]?.includes('fleetNumber') ? (
                  <span style={{ color: 'red' }}>{errorMessages.fleetNumber}</span>
                ) : null}
              </div>
            )}
          </div>

          <div className={style.vehicleBox}>
            {isRequired('licensePlateState') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>License Plate State</label>
                <input
                  required
                  className={style.vehicleOptionsInput}
                  type="text"
                  value={vehiclePass.vehicle?.licensePlateState}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e => handleChange(passIndex, 'licensePlateState', e.target.value)}
                  placeholder="Enter your License Plate State"
                />
                {errors?.[passInfoId]?.[passIndex]?.includes('licensePlateState') ? (
                  <span style={{ color: 'red' }}>{errorMessages.licensePlateState}</span>
              ) : null}
              </div>
            )}
            {isRequired('licensePrimaryDriver') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>
                  Primary Driver License
                </label>
                <input
                  required
                  className={style.vehicleOptionsInput}
                  type="text"
                  value={vehiclePass.vehicle?.licensePrimaryDriver}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e =>
                    handleChange(passIndex, 'licensePrimaryDriver', e.target.value)
                  }
                  placeholder="Enter primary driver's license"
                />
                {errors?.[passInfoId]?.[passIndex]?.includes('licensePrimaryDriver') ? (
                  <span style={{ color: 'red' }}>
                    {errorMessages.licensePrimaryDriver}
                  </span>
                ) : null}
              </div>
            )}
            {isRequired('primaryDriverName') && (
              <div className={style.vehicleOptions}>
                <label className={style.vehicleOptionslabel}>
                  Primary Driver Name
                </label>
                <input
                  required
                  className={style.vehicleOptionsInput}
                  type="text"
                  value={vehiclePass.vehicle?.primaryDriverName}
                  // disabled={vehiclePass.status === 'inactive'}
                  disabled={disabled}
                  onChange={e =>
                    handleChange(passIndex, 'primaryDriverName', e.target.value)
                  }
                  placeholder="Enter primary driver's Name"
                />
                {errors?.[passInfoId]?.[passIndex]?.includes('primaryDriverName') ? (
                  <span style={{ color: 'red' }}>
                    {errorMessages.primaryDriverName}
                  </span>
                ) : null}
              </div>
            )}
          </div>

          <hr className={style.horizontalLine} />
          <div className={style.vehicleBox}>
            <div className={style.box__inputField}>
              <label htmlFor="check-out-inpt" className={style.label}>
                Valid starting on <span style={{ color: 'red' }}>*</span>
              </label>
              <div className={style.inputBox}>
                <InputField
                  required
                  smallSize
                  htmlFor={`startDate_${passInfoId}_${passIndex}-inpt`}
                  inputType="date"
                  inputValue={startDate}
                  highlightOnFocus
                  changeHandler={handleStartDateChange}
                  inputPlaceholder="Choose the date"
                  min={initialStartDate}
                  max={initialEndDate}
                  disabled={disabled}
                />
              </div>
            </div>
            <div className={style.box__inputField}>
              <label htmlFor="check-out-inpt" className={style.label}>
                Valid through <span style={{ color: 'red' }}>*</span>
              </label>
              <div className={style.inputBox}>
                <InputField
                  required
                  smallSize
                  htmlFor={`endDate_${passInfoId}_${passIndex}-inpt`}
                  inputType="date"
                  inputValue={endDate}
                  highlightOnFocus
                  changeHandler={handleEndDateChange}
                  inputPlaceholder="Choose the date"
                  min={startDate}
                  max={initialEndDate}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditVehiclePassForm;
